import { useCallback, useEffect, useMemo, useState } from 'react'
import {
   Breadcrumb,
   Button,
   Card,
   Col,
   Form,
   Input,
   notification,
   Radio,
   Row,
   Select,
   Typography,
   Checkbox,
   message
} from 'antd'
import { EditOutlined, HomeOutlined, LeftCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import { useStore } from 'src/hooks'
import { debounce, trim } from 'lodash'
import FloatLabel from 'src/common/components/float-label'
import { useTranslation } from 'react-i18next'
import i18n from 'src/helpers/i18n'
import { createValidationRules } from 'src/helpers/validateRule'
import { observer } from 'mobx-react-lite'
import { useLoading } from 'src/common/components/LoadingContainer'
import { CONFIG_URL, ROLE_ACCOUNT } from 'src/helpers/constants'
import { validatePassword, validatePhone } from 'src/helpers/functions'
import UploadableImageBox from './UploadableImageBox'

const { Title } = Typography

const AccountCreateScreen = observer(() => {
   const history = useHistory()
   const [form] = Form.useForm()
   const UserStore = useStore('UserStore')
   const { t } = useTranslation()
   const { id } = useParams<{ id: string }>()
   const { showLoading, hideLoading } = useLoading()
   const [detail, setDetail] = useState<any>(null)
   const [selectedAccount, setSelectedAccount] = useState<string | null>(null)
   const [isChangePassword, setIsChangePassword] = useState(false)
   const [status, setStatus] = useState(1)
   const [licenseImage, setLicenseImage] = useState<File | string>() //image1
   const [frontImage, setFrontImage] = useState<File | string>() //image2
   const [bodyImage, setBodyImage] = useState<File | string>() //image3

   const readOnly = useMemo(() => history.location.pathname.includes('detail'), [history.location])
   const rule = useMemo(() => createValidationRules(), [i18n.language])

   useEffect(() => {
      if (id && id !== 'create') {
         handleGetDetail()
      }
   }, [id])

   const handleGetDetail = useCallback(async () => {
      if (!id) return
      try {
         showLoading()
         if (UserStore.userSelected) {
            setDetail(UserStore.userSelected)
            setSelectedAccount(UserStore.userSelected?.role)
            setStatus(UserStore.userSelected?.status ?? 1)
            form.setFieldsValue({
               ...UserStore.userSelected,
               password: '',
               isEmailVerified: UserStore.userSelected?.isEmailVerified
            })
            setLicenseImage(UserStore.userSelected.image1 || '')
            setFrontImage(UserStore.userSelected.image2 || '')
            setBodyImage(UserStore.userSelected.image3 || '')
         }
      } finally {
         hideLoading()
      }
   }, [])

   const onFinish = async (values: any) => {
      console.log('values', values)

      let body: any = {
         ...values,
         status
      }
      if (!values.address || trim(values.address) == '') {
         body.address = ''
      }
      if (selectedAccount === ROLE_ACCOUNT.DRIVER) {
         if (!licenseImage) {
            message.error('Bằng lái xe bắt buộc phải tải lên')
            return
         }
         if (!frontImage) {
            message.error('Ảnh trước xe bắt buộc phải tải lên')
            return
         }
         if (!bodyImage) {
            message.error('Ảnh thân xe bắt buộc phải tải lên')
            return
         }
         body.image1 = licenseImage
         body.image2 = frontImage
         body.image3 = bodyImage
      }
      if (readOnly && !isChangePassword) {
         delete body.password
      }
      if (!trim(values?.password)) {
         delete body.password
      }
      delete body['changePassword']
      delete body['confirm_password']
      // if (id) {
      //    delete body['address']
      // }

      if (selectedAccount !== ROLE_ACCOUNT.DRIVER) {
         body.isEmailVerified = true
      }

      const data = id ? await UserStore.updateEmployees(body, id) : await UserStore.createEmployees(body)

      if (data) {
         notification.open({
            message: t('notification'),
            description: id ? t('update_successfully') : t('create_successfully'),
            type: 'success',
            placement: 'topRight'
         })
         history.goBack()
      }
   }

   const renderPasswordSection = () => {
      // Case CREATE: luôn hiển thị mật khẩu
      if (!id) {
         return (
            <Row gutter={12} className="gx-flex-row">
               <Col xs={24} md={12}>
                  <FloatLabel label={'Mật khẩu'} required>
                     <Form.Item
                        name="password"
                        rules={[
                           rule.requiredEnter,
                           rule.maxRuleCharacter50,
                           () => ({
                              validator(_, value) {
                                 if (validatePassword(value)) return Promise.resolve()
                                 return Promise.reject('Mật khẩu phải có cả chữ và số, tối thiểu 8 kí tự')
                              }
                           })
                        ]}
                     >
                        <Input.Password placeholder={t('enter')} maxLength={50} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
               <Col xs={24} md={12}>
                  <FloatLabel label={'Xác nhận mật khẩu'} required>
                     <Form.Item
                        name="confirm_password"
                        dependencies={['password']}
                        rules={[
                           rule.requiredEnter,
                           ({ getFieldValue }) => ({
                              validator(_, value) {
                                 if (!value || getFieldValue('password') === value) return Promise.resolve()
                                 return Promise.reject('Mật khẩu không trùng khớp')
                              }
                           })
                        ]}
                     >
                        <Input.Password placeholder={t('enter')} maxLength={50} />
                     </Form.Item>
                  </FloatLabel>
               </Col>
            </Row>
         )
      }

      // Case DETAIL: có checkbox "Đổi mật khẩu"
      return (
         <>
            <Row gutter={12}>
               <Form.Item label="Đổi mật khẩu" name="changePassword" valuePropName="checked">
                  <Checkbox checked={isChangePassword} onChange={(e) => setIsChangePassword(e.target.checked)}>
                     {t('ok')}
                  </Checkbox>
               </Form.Item>
               {isChangePassword && (
                  <Row gutter={12} className="gx-flex-row">
                     <Col xs={24} md={12}>
                        <FloatLabel label={'Mật khẩu mới'}>
                           <Form.Item
                              name="password"
                              rules={[
                                 () => ({
                                    validator(_, value) {
                                       if (!value || validatePassword(value)) return Promise.resolve()
                                       return Promise.reject('Mật khẩu phải có cả chữ và số, tối thiểu 8 kí tự')
                                    }
                                 })
                              ]}
                           >
                              <Input.Password placeholder={t('enter')} maxLength={50} />
                           </Form.Item>
                        </FloatLabel>
                     </Col>
                     <Col xs={24} md={12}>
                        <FloatLabel label={'Xác nhận mật khẩu mới'}>
                           <Form.Item
                              name="confirm_password"
                              dependencies={['password']}
                              rules={[
                                 ({ getFieldValue }) => ({
                                    validator(_, value) {
                                       if (!value || getFieldValue('password') === value) return Promise.resolve()
                                       return Promise.reject('Mật khẩu không trùng khớp')
                                    }
                                 })
                              ]}
                           >
                              <Input.Password placeholder={t('enter')} maxLength={50} />
                           </Form.Item>
                        </FloatLabel>
                     </Col>
                  </Row>
               )}
            </Row>
            <Form.Item label="Trạng thái" name="status" valuePropName="checked" initialValue={status === 1}>
               <Checkbox checked={status === 1} onChange={(e) => setStatus(e.target.checked ? 1 : 0)}>
                  {status === 1 ? 'Hoạt động' : 'Dừng hoạt động'}
               </Checkbox>
            </Form.Item>
         </>
      )
   }

   const renderContent = () => (
      <Card style={{ width: '100%', marginTop: '20px' }}>
         {/* Thông tin cơ bản */}
         <Row gutter={12} className="gx-flex-row">
            <Col xs={24} md={12}>
               <FloatLabel label={'Loại Tài khoản'} required>
                  <Form.Item name="role" rules={[rule.requiredEnter]}>
                     <Select
                        placeholder={'Chọn loại tài khoản'}
                        style={{ width: '100%' }}
                        //@ts-ignore
                        onChange={(value) => setSelectedAccount(value)}
                     >
                        <Select.Option value={ROLE_ACCOUNT.USER}>Khách hàng</Select.Option>
                        <Select.Option value={ROLE_ACCOUNT.DRIVER}>Tài xế</Select.Option>
                        <Select.Option value={ROLE_ACCOUNT.SALE}>Chăm sóc khách hàng</Select.Option>
                        <Select.Option value={ROLE_ACCOUNT.ADMIN}>Admin</Select.Option>
                     </Select>
                  </Form.Item>
               </FloatLabel>
            </Col>
            <Col xs={24} md={12}>
               <FloatLabel label={'Họ và tên'} required>
                  <Form.Item name="name" rules={[rule.requiredEnter, rule.maxRuleCharacter50]}>
                     <Input placeholder={t('enter')} maxLength={50} />
                  </Form.Item>
               </FloatLabel>
            </Col>
         </Row>
         <Row gutter={12} className="gx-flex-row">
            <Col xs={24} md={12}>
               <FloatLabel label={'Số điện thoại'} required>
                  <Form.Item
                     name="phoneNumber"
                     rules={[
                        rule.maxRuleCharacter10,
                        () => ({
                           validator(_, value) {
                              if (validatePhone(value)) return Promise.resolve()
                              return Promise.reject('Số điện thoại phải là số và chứa 10 chữ số')
                           }
                        })
                     ]}
                  >
                     <Input placeholder={'0911112222'} maxLength={10} />
                  </Form.Item>
               </FloatLabel>
            </Col>
            <Col xs={24} md={12}>
               <FloatLabel label={'Địa chỉ'}>
                  <Form.Item name="address" rules={[rule.maxRuleCharacter200]}>
                     <Input placeholder={t('enter')} maxLength={200} />
                  </Form.Item>
               </FloatLabel>
            </Col>
         </Row>

         {/* Password / Change Password */}
         {renderPasswordSection()}
         {/* Chỉ hiển thị khi là tài xế */}
         {selectedAccount === ROLE_ACCOUNT.DRIVER && (
            <>
               <Row gutter={16}>
                  <Col span={24} style={{ marginTop: '10px' }}>
                     <FloatLabel label={'Ảnh xác thực'} required>
                        <Row className="gx-flex-row" style={{ flex: 1, alignItems: 'center', paddingTop: 20 }}>
                           <UploadableImageBox
                              key={'licenseImage'}
                              value={licenseImage}
                              onChange={setLicenseImage}
                              label="Bằng lái"
                           />
                           <UploadableImageBox
                              key={'frontImage'}
                              value={frontImage}
                              onChange={setFrontImage}
                              label="Ảnh trước xe"
                           />
                           <UploadableImageBox
                              key={'bodyImage'}
                              value={bodyImage}
                              onChange={setBodyImage}
                              label="Ảnh thân xe"
                           />
                        </Row>
                     </FloatLabel>
                  </Col>
               </Row>
               <Row gutter={24}>
                  <Col xs={24}>
                     <FloatLabel label={'Xác thực tài khoản'} required>
                        <Form.Item
                           name="isEmailVerified"
                           initialValue={UserStore.userSelected?.isEmailVerified ?? false}
                        >
                           <Radio.Group
                              disabled={readOnly}
                              defaultValue={UserStore.userSelected?.isEmailVerified ?? false}
                           >
                              <Radio value={true}>Đã xác thực</Radio>
                              <Radio value={false}>Chưa xác thực</Radio>
                           </Radio.Group>
                        </Form.Item>
                     </FloatLabel>
                  </Col>
               </Row>
            </>
         )}
      </Card>
   )

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Quản lý tài khoản'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item onClick={() => history.goBack()} className="gx-link">
                     {'Quản lý tài khoản'}
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{id ? (readOnly ? t('details') : t('edit')) : t('add_new')}</Breadcrumb.Item>
               </Breadcrumb>
            </Col>
         </Row>

         <Form form={form} layout="vertical" onFinish={debounce(onFinish, 600)} initialValues={detail || {}}>
            {renderContent()}
            <Form.Item style={{ textAlign: 'center' }}>
               <Button
                  icon={<LeftCircleOutlined />}
                  type="dashed"
                  style={{ color: '#ED5C19' }}
                  onClick={() => history.goBack()}
                  className="gx-mr-2"
               >
                  {t('txt_back')}
               </Button>
               {!readOnly && (
                  <Button
                     icon={id ? <EditOutlined /> : <PlusCircleOutlined />}
                     type="primary"
                     htmlType="submit"
                     className="gx-mr-2"
                  >
                     {id ? t('update') : t('create')}
                  </Button>
               )}
            </Form.Item>
         </Form>
      </div>
   )
})

export default AccountCreateScreen
